import type { SignalItem } from "@/composables/useCarProperty";
import { getMockDataByKeys } from "./mock";

/** 沿航向角方向移动距离（米），返回新经纬度 */
function movePosition(
  lat: number,
  lng: number,
  headingDeg: number,
  distanceM: number
): { lat: number; lng: number } {
  const rad = (headingDeg * Math.PI) / 180;
  const R = 6371000;
  const dLat = ((distanceM * Math.cos(rad)) / R) * (180 / Math.PI);
  const dLng =
    ((distanceM * Math.sin(rad)) / (R * Math.cos((lat * Math.PI) / 180))) *
    (180 / Math.PI);
  return { lat: lat + dLat, lng: lng + dLng };
}

/**
 * 预瞄信息页订阅信号
 */
export const SUBSCRIBE_SIGNALS: SignalItem[] = [
  // 经纬度与航向
  {
    signalName: "SOA::INSInfo_MSG_InsLatitude",
    displayName: "INS_Latitude",
  },
  {
    signalName: "SOA::INSInfo_MSG_InsLongitude",
    displayName: "INS_Longitude",
  },
  {
    signalName: "SOA::INSInfo_MSG_InsHeadingAngle",
    displayName: "INS_HeadingAngle",
  },
  // 车速
  {
    signalName: "SOA::VehicleDriveState_MSG_VehSpd",
    displayName: "VehSpd",
  },
  // 预瞄数组 FL/FR/RL/RR
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_FwdRoadFeat_FL",
    displayName: "MSG_PCM_FwdRoadFeat_FL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_FwdRoadFeat_FR",
    displayName: "MSG_PCM_FwdRoadFeat_FR",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_FwdRoadFeat_RL",
    displayName: "MSG_PCM_FwdRoadFeat_RL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_FwdRoadFeat_RR",
    displayName: "MSG_PCM_FwdRoadFeat_RR",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjHght_FL",
    displayName: "MSG_PCM_SusObjHght_FL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjHght_FR",
    displayName: "MSG_PCM_SusObjHght_FR",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjHght_RL",
    displayName: "MSG_PCM_SusObjHght_RL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjHght_RR",
    displayName: "MSG_PCM_SusObjHght_RR",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjLengh_FL",
    displayName: "MSG_PCM_SusObjLengh_FL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjLengh_FR",
    displayName: "MSG_PCM_SusObjLengh_FR",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjLengh_RL",
    displayName: "MSG_PCM_SusObjLengh_RL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjLengh_RR",
    displayName: "MSG_PCM_SusObjLengh_RR",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjDist_FL",
    displayName: "MSG_PCM_SusObjDist_FL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjDist_FR",
    displayName: "MSG_PCM_SusObjDist_FR",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjDist_RL",
    displayName: "MSG_PCM_SusObjDist_RL",
  },
  {
    signalName: "SOA::SusPreviewInfo_MSG_PCM_SusObjDist_RR",
    displayName: "MSG_PCM_SusObjDist_RR",
  },
  // 垂向加速度
  {
    signalName: "SOA::SuspensionSensorInfo_MSG_SusIMUAccVert",
    displayName: "SusIMUAccVert",
  },
];

export const SEND_SIGNALS: SignalItem[] = [];

/** 仅对未在下面 return 里手写的字段走正弦 mock；PCM/INS/VehSpd 均由本函数覆盖 */
const MOCK_KEYS_FOR_SINE = ["SusIMUAccVert"] as const;

/** 预瞄类型 1-12 的 hght/lengh 映射（对应 previewFeatureMap） */
const MOCK_FEATURE_MAP: Record<number, { hght: number; lengh: number }> = {
  1: { hght: 50, lengh: 500 }, // 减速带
  2: { hght: -30, lengh: 1000 }, // 井盖
  3: { hght: 40, lengh: 400 }, // 破损路面
  4: { hght: 15, lengh: 200 }, // 接缝
  5: { hght: 80, lengh: 800 }, // 上坡
  6: { hght: -60, lengh: 600 }, // 下坡
  7: { hght: 35, lengh: 1200 }, // 长波路
  8: { hght: 45, lengh: 600 }, // 连续减速带
  9: { hght: 55, lengh: 350 }, // 凸起(预瞄)
  10: { hght: -45, lengh: 350 }, // 凹陷(预瞄)
  11: { hght: 60, lengh: 400 }, // 凸起(地图)
  12: { hght: -50, lengh: 400 }, // 凹陷(地图)
};

/** Mock 目标物 1 类型：1=减速带 2=井盖 ... 12=凹陷(地图) */
const MOCK_FEAT_TYPE = 1;
/** 与 helper 中 PREVIEW_WHEELBASE_MM 一致：Mock 后轮距离 = 前轮 + 轴距 */
const MOCK_PREVIEW_WHEELBASE_MM = 3100;

/** Mock 目标物 2 类型：1=减速带 2=井盖 ... 12=凹陷(地图) */
const MOCK_FEAT_TYPE_2 = 2;
const MOCK_FEAT_CURRENT =
  MOCK_FEATURE_MAP[MOCK_FEAT_TYPE] ?? MOCK_FEATURE_MAP[1];
const MOCK_FEAT_CURRENT_2 =
  MOCK_FEATURE_MAP[MOCK_FEAT_TYPE_2] ?? MOCK_FEATURE_MAP[2];

/** Mock 车速 km/h，修改此值即可，其他衍生值自动跟随 */
const MOCK_SPEED_KMH = 5;

/** 回调间隔 50ms */
const MOCK_INTERVAL_MS = 50;
/** 50ms 内行驶距离 mm = (km/h ÷ 3.6) × 0.05s × 1000 */
const MOCK_DIST_DECREASE_MM = Math.round(
  (MOCK_SPEED_KMH / 3.6) * (MOCK_INTERVAL_MS / 1000) * 1000
);
/** 50ms 内行驶距离 m，用于经纬度更新 */
const MOCK_MOVE_DISTANCE_M = (MOCK_SPEED_KMH / 3.6) * (MOCK_INTERVAL_MS / 1000);

/** 10m = 10000 mm */
const MOCK_MAX_DIST_MM = 10000;

/** Mock 状态：车辆位置、两个目标物（分别占数组 0、1 位） */
let mockVehicleState: {
  lat: number;
  lng: number;
  heading: number;
  dist1: number;
  dist2: number;
} | null = null;

function initMockState(): NonNullable<typeof mockVehicleState> {
  if (!mockVehicleState) {
    mockVehicleState = {
      lat: 40.1683718,
      lng: 116.573723,
      heading: -96.918258,
      dist1: MOCK_MAX_DIST_MM,
      dist2: 6000,
    };
  }
  return mockVehicleState;
}

/**
 * 预瞄 mock：双目标占数组 0、1 位，距离随 MOCK_INTERVAL_MS 递减，出车后从 10m 重置；
 * 车辆经纬度沿航向前进；竖直加速度等与 `getMockDataByKeys` 正弦相关。
 * 调参：`MOCK_SPEED_KMH`、`MOCK_FEAT_TYPE` / `MOCK_FEAT_TYPE_2`、`MOCK_MAX_DIST_MM`。
 */
export function generateMockPreviewInfoData(): DomianControl.PreviewInfoNotifyData {
  const base = getMockDataByKeys([...MOCK_KEYS_FOR_SINE], {
    SusIMUAccVert: [-1, 1],
  });

  const state = initMockState();

  state.dist1 -= MOCK_DIST_DECREASE_MM;
  state.dist2 -= MOCK_DIST_DECREASE_MM;
  if (state.dist1 <= 0) state.dist1 = MOCK_MAX_DIST_MM;
  if (state.dist2 <= 0) state.dist2 = MOCK_MAX_DIST_MM;

  const moved = movePosition(
    state.lat,
    state.lng,
    state.heading,
    MOCK_MOVE_DISTANCE_M
  );
  state.lat = moved.lat;
  state.lng = moved.lng;

  const featFL = Array(10).fill(0);
  const featFR = Array(10).fill(0);
  const hghtFL = Array(10).fill(0);
  const hghtFR = Array(10).fill(0);
  const lenghFL = Array(10).fill(0);
  const lenghFR = Array(10).fill(0);
  const distFL = Array(10).fill(0);
  const distFR = Array(10).fill(0);

  if (state.dist1 > 0 && state.dist1 <= MOCK_MAX_DIST_MM) {
    featFL[0] = featFR[0] = MOCK_FEAT_TYPE;
    hghtFL[0] = hghtFR[0] = MOCK_FEAT_CURRENT.hght;
    lenghFL[0] = lenghFR[0] = MOCK_FEAT_CURRENT.lengh;
    distFL[0] = distFR[0] = state.dist1;
  }
  if (state.dist2 > 0 && state.dist2 <= MOCK_MAX_DIST_MM) {
    featFL[1] = featFR[1] = MOCK_FEAT_TYPE_2;
    hghtFL[1] = hghtFR[1] = MOCK_FEAT_CURRENT_2.hght;
    lenghFL[1] = lenghFR[1] = MOCK_FEAT_CURRENT_2.lengh;
    distFL[1] = distFR[1] = state.dist2;
  }

  const distRL = Array(10).fill(0);
  const distRR = Array(10).fill(0);
  for (let i = 0; i < 10; i++) {
    if (distFL[i] > 0) {
      distRL[i] = distFL[i] + MOCK_PREVIEW_WHEELBASE_MM;
    }
    if (distFR[i] > 0) {
      distRR[i] = distFR[i] + MOCK_PREVIEW_WHEELBASE_MM;
    }
  }

  return {
    ...base,
    INS_Latitude: state.lat,
    INS_Longitude: state.lng,
    INS_HeadingAngle: state.heading,
    VehSpd: MOCK_SPEED_KMH,
    MSG_PCM_FwdRoadFeat_FL: featFL,
    MSG_PCM_FwdRoadFeat_FR: featFR,
    MSG_PCM_FwdRoadFeat_RL: [...featFL],
    MSG_PCM_FwdRoadFeat_RR: [...featFR],
    MSG_PCM_SusObjHght_FL: hghtFL,
    MSG_PCM_SusObjHght_FR: hghtFR,
    MSG_PCM_SusObjHght_RL: [...hghtFL],
    MSG_PCM_SusObjHght_RR: [...hghtFR],
    MSG_PCM_SusObjLengh_FL: lenghFL,
    MSG_PCM_SusObjLengh_FR: lenghFR,
    MSG_PCM_SusObjLengh_RL: [...lenghFL],
    MSG_PCM_SusObjLengh_RR: [...lenghFR],
    MSG_PCM_SusObjDist_FL: distFL,
    MSG_PCM_SusObjDist_FR: distFR,
    MSG_PCM_SusObjDist_RL: distRL,
    MSG_PCM_SusObjDist_RR: distRR,
  } as DomianControl.PreviewInfoNotifyData;
}
